/**
 * API Service Layer
 * Typed functions for all Hoku Backend API endpoints
 *
 * All requests go through /api/proxy which:
 * - Forwards to NEXT_PUBLIC_API_URL
 * - Attaches X-User-ID, X-User-Email, X-User-Role headers from session
 */

import { apiFetch, apiFormFetch, buildUrl, PROXY_BASE, AI_TIMEOUT } from "@/shared/api/client";

import type {
  ApiResponse,
  ApiError,
  DailyDripRequest,
  DailyDripResponse,
  HealthResponse,
  MoodsParams,
  MoodOption,
  SkinAnalysis,
  SkinAnalysisResult,
  StyleBrand,
  UserInsights,
  WeatherParams,
  WeatherResponse,
  WeatherSuggestionsResponse,
  Coupon,
  CouponValidationResponse,
  CouponRedemption,
  PaginationMeta,
} from "@/types/api";

// ============================================
// Health Check
// ============================================

export async function getHealth(): Promise<ApiResponse<HealthResponse>> {
  return apiFetch(`${PROXY_BASE}/health`);
}

// ============================================
// Wardrobe (re-exported from feature)
// ============================================

export {
  getWardrobeItems,
  getWardrobeStats,
  getWardrobeItem,
  deleteWardrobeItem,
  updateWardrobeItem,
  onboardClothes,
  onboardClothesBatch,
  analyzeForSet,
  confirmOnboard,
  getUploadQuota,
  getPods,
  ensureDefaultPods,
  getPod,
  createPod,
  renamePod,
  deletePod,
  addItemToPod,
  removeItemFromPod,
  toggleFavorite,
} from "@/features/wardrobe/api";

export type {
  PodItem,
  Pod,
  PodWithItems,
} from "@/features/wardrobe/types";

// ============================================
// Daily Drip
// ============================================

/**
 * Generate daily outfit suggestions
 * Cost: 7 Hoku Coins
 */
export async function generateDailyDrip(
  request: DailyDripRequest
): Promise<ApiResponse<DailyDripResponse>> {
  return apiFetch(`${PROXY_BASE}/daily-drip`, {
    method: "POST",
    body: JSON.stringify(request),
    timeout: AI_TIMEOUT,
  });
}

/**
 * Fetch the user's STORED Daily Drip (pre-generated by the morning cron, or the
 * last on-demand one). Instant — no AI call, no credits. `hasDrip:false` means
 * none exists yet, and the client falls back to generating on-demand.
 */
export async function getCurrentDailyDrip(): Promise<{
  success?: boolean;
  hasDrip?: boolean;
  outfit?: any | null;
}> {
  return apiFetch(`${PROXY_BASE}/daily-drip/current`, { method: "GET" });
}

// ============================================
// Moods
// ============================================

/**
 * Generate personalized mood options (AI-powered)
 */
export async function getMoods(
  params?: MoodsParams
): Promise<ApiResponse<{ moods: MoodOption[]; generatedAt: string; context: unknown }>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/moods`, params as Record<string, unknown>), {
    timeout: AI_TIMEOUT,
  });
}

/**
 * Get default mood options (no AI, fast)
 */
export async function getDefaultMoods(): Promise<ApiResponse<{ moods: MoodOption[] }>> {
  return apiFetch(`${PROXY_BASE}/moods/defaults`);
}

// ============================================
// Outfits (re-exported from feature)
// ============================================

export {
  getOutfits,
  getOutfit,
  getExistingOutfits,
  generateOutfit,
  deleteOutfit,
  toggleOutfitLike,
  toggleOutfitDislike,
} from "@/features/look-lab/api";

// ============================================
// Weather
// ============================================

/**
 * Get current weather data (free)
 */
export async function getWeather(
  params?: WeatherParams
): Promise<ApiResponse<WeatherResponse>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/weather`, params as Record<string, unknown>));
}

/**
 * Get weather-based clothing suggestions (free)
 */
export async function getWeatherSuggestions(
  params?: WeatherParams
): Promise<ApiResponse<WeatherSuggestionsResponse>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/weather/suggestions`, params as Record<string, unknown>));
}

// ============================================
// Style Station (re-exported from feature)
// ============================================

export {
  getStyleHistory,
  generateStyle,
  getStyle,
  deleteStyle,
  toggleStyleLike,
} from "@/features/style-station/api";

// ============================================
// Shopping (re-exported from feature)
// ============================================

export {
  getShoppingHistory,
  generateWardrobeShopping,
  generateImageShopping,
  getShoppingWishlist,
  addToShoppingWishlist,
  toggleShoppingWishlist,
  removeFromShoppingWishlist,
} from "@/features/shopping/api";

export type {
  ShoppingWishlistItem,
  ShoppingWishlistResponse,
  AddToShoppingWishlistRequest,
  ToggleShoppingWishlistResponse,
} from "@/features/shopping/types";

// ============================================
// User Profile
// ============================================

/**
 * Get saved skin analysis
 */
export async function getSkinAnalysis(
  userId: string
): Promise<ApiResponse<SkinAnalysis>> {
  return apiFetch(`${PROXY_BASE}/users/${userId}/skin-analysis`);
}

/**
 * Get skin analysis usage (free uses remaining this month)
 */
export async function getSkinAnalysisUsage(
  userId: string
): Promise<ApiResponse<{ usedThisMonth: number; freeLimit: number; freeRemaining: number }>> {
  return apiFetch(`${PROXY_BASE}/users/${userId}/skin-analysis/usage`);
}

/**
 * Analyze skin tone from image
 * @param userId User ID
 * @param image Image file to analyze
 * @returns Skin analysis result
 */
export async function analyzeSkin(
  userId: string,
  image: File
): Promise<ApiResponse<SkinAnalysisResult>> {
  const formData = new FormData();
  formData.append("image", image);

  // Use apiFormFetch for multipart/form-data (not apiFetch which sets Content-Type: application/json)
  return apiFormFetch(`${PROXY_BASE}/users/${userId}/skin-analysis`, formData);
}

/**
 * Manually update skin tone (Monk scale adjustment)
 */
export async function updateSkinTone(
  userId: string,
  monkScale: string
): Promise<ApiResponse<SkinAnalysis>> {
  return apiFetch(`${PROXY_BASE}/users/${userId}/skin-analysis/tone`, {
    method: "PATCH",
    body: JSON.stringify({ monkScale }),
  });
}

/**
 * Upload profile photo
 */
export async function uploadProfilePhoto(
  userId: string,
  image: File
): Promise<ApiResponse<{ profileImageUrl: string }>> {
  const formData = new FormData();
  formData.append("image", image);
  return apiFormFetch(`${PROXY_BASE}/users/${userId}/profile/photo`, formData);
}

/**
 * Get saved style brand
 */
export async function getStyleBrand(
  userId: string
): Promise<ApiResponse<StyleBrand>> {
  return apiFetch(`${PROXY_BASE}/users/${userId}/brand`);
}

/**
 * Get cached preference insights
 */
export async function getUserInsights(
  userId: string
): Promise<ApiResponse<UserInsights>> {
  return apiFetch(`${PROXY_BASE}/users/${userId}/insights`);
}

// ============================================
// User Profile
// ============================================

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  profileImageUrl: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  city: string | null;
  country: string | null;
  phoneNumber: string | null;
  bodyType: string | null;
  heightRange: string | null;
  weightRange: string | null;
  topSize: string | null;
  bottomSize: string | null;
  skinTone: string | null;
  undertone: string | null;
  stylePreferences: string[] | null;
  favoriteColors: string[] | null;
  fashionGoals: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileData {
  name?: string;
  gender?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  city?: string;
  country?: string;
  bodyType?: string;
  heightRange?: string;
  weightRange?: string;
  topSize?: string;
  bottomSize?: string;
  skinTone?: string;
  undertone?: string;
  stylePreferences?: string[];
  favoriteColors?: string[];
  fashionGoals?: string[];
}

/**
 * Get user profile
 */
export async function getUserProfile(
  userId: string
): Promise<ApiResponse<UserProfile>> {
  return apiFetch(`${PROXY_BASE}/users/${userId}/profile`);
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string,
  data: UpdateProfileData
): Promise<ApiResponse<UserProfile>> {
  return apiFetch(`${PROXY_BASE}/users/${userId}/profile`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// Wardrobe Onboarding - re-exported from features/wardrobe/api above

// ============================================
// Utility Functions
// ============================================

/**
 * Check if API response is successful
 */
export function isApiSuccess<T>(response: ApiResponse<T>): response is { success: true; data: T } {
  return response.success === true;
}

/**
 * Check if API response is an error
 */
export function isApiError(response: ApiResponse<unknown>): response is ApiError {
  return response.success === false;
}

/**
 * Extract error message from API response
 */
export function getApiError(response: ApiResponse<unknown>): string {
  if (response.success) return "";
  return response.message || response.error || "Unknown error";
}

/**
 * Get status code from error response
 */
export function getApiErrorCode(response: ApiResponse<unknown>): number | undefined {
  if (response.success) return undefined;
  return (response as ApiError).statusCode;
}

/**
 * Check if error is due to insufficient credits
 */
export function isInsufficientCredits(response: ApiResponse<unknown>): boolean {
  if (response.success) return false;
  return (response as ApiError).statusCode === 402;
}

/**
 * Get credit info from insufficient credits error
 */
export function getCreditInfo(response: ApiResponse<unknown>): {
  required: number;
  available: number;
  featureCode: string;
} | null {
  if (response.success) return null;
  const error = response as ApiError & {
    requiredCredits?: number;
    availableCredits?: number;
    featureCode?: string;
  };
  if (error.statusCode !== 402) return null;
  return {
    required: error.requiredCredits || 0,
    available: error.availableCredits || 0,
    featureCode: error.featureCode || "",
  };
}

// ============================================
// Billing
// ============================================

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  price: number;
  currency: string;
  creditsGranted: number;
  dressUploadsGranted: number;
  sortOrder: number;
}

export interface UsageItem {
  id: string;
  feature_name: string;
  feature_code: string | null;
  credits_spent: number;
  balance_after: number;
  created_at: string;
}

export interface CreditBalance {
  balance: number;
  totalEarned: number;
  totalSpent: number;
  lastTransactionAt: string | null;
}

export interface FeatureInfo {
  id: string;
  featureCode: string;
  featureName: string;
  description: string | null;
  creditCost: number;
}

/**
 * Get all available plans
 */
export async function getPlans(): Promise<ApiResponse<Plan[]>> {
  return apiFetch(`${PROXY_BASE}/billing/plans`);
}

/**
 * Get user's credit usage history
 */
export async function getUsageHistory(params?: {
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<UsageItem[]>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/billing/usage`, params as Record<string, unknown>));
}

/**
 * Get user's current credit balance
 */
export async function getCreditBalance(): Promise<ApiResponse<CreditBalance>> {
  return apiFetch(`${PROXY_BASE}/billing/balance`);
}

/**
 * Get all features with their credit costs
 */
export async function getFeatures(): Promise<ApiResponse<FeatureInfo[]>> {
  return apiFetch(`${PROXY_BASE}/billing/features`);
}

// getUploadQuota - re-exported from features/wardrobe/api above

// ============================================
// Razorpay Payment
// ============================================

export interface RazorpayOrder {
  id: string;
  entity: string;
  amount: number;
  amount_paid: number;
  amount_due: number;
  currency: string;
  receipt: string;
  status: string;
  notes: Record<string, string>;
}

export interface CreateOrderResponse {
  order: RazorpayOrder;
  plan: {
    id: string;
    name: string;
    displayName: string;
    creditsGranted: number;
  };
}

export interface VerifyPaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  /**
   * The GA cookie's client id. GA4's Measurement Protocol requires one for web
   * streams — an event without it is accepted and then silently discarded — and
   * the server has no other way to obtain it.
   */
  ga_client_id?: string;
}

export interface VerifyPaymentResponse {
  message: string;
  creditsAdded: number;
  /**
   * True when the backend already reported the purchase to GA4. The browser
   * then must NOT report it as well; exactly one of the two does, so revenue is
   * never double counted. False while server-side GA4 is unconfigured.
   */
  analyticsSent?: boolean;
}

/**
 * Create a Razorpay order for plan purchase
 */
export async function createRazorpayOrder(planId: string, couponCode?: string): Promise<ApiResponse<CreateOrderResponse>> {
  return apiFetch(`${PROXY_BASE}/billing/razorpay/create-order`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId, ...(couponCode ? { coupon_code: couponCode } : {}) }),
  });
}

/**
 * Verify Razorpay payment after successful payment
 */
export async function verifyRazorpayPayment(data: VerifyPaymentRequest): Promise<ApiResponse<VerifyPaymentResponse>> {
  return apiFetch(`${PROXY_BASE}/billing/razorpay/verify-payment`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Coupons
// ============================================

/**
 * Validate a coupon code and get discount info for all plans
 */
export async function validateCoupon(code: string): Promise<ApiResponse<CouponValidationResponse>> {
  return apiFetch(`${PROXY_BASE}/billing/coupons/validate`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/**
 * Redeem a 100% off coupon (skip Razorpay entirely)
 */
export async function redeemFreePlan(planId: string, couponCode: string): Promise<ApiResponse<{ message: string; creditsAdded: number; uploadsAdded: number }>> {
  return apiFetch(`${PROXY_BASE}/billing/redeem-free`, {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId, coupon_code: couponCode }),
  });
}

// ============================================
// Admin Coupons
// ============================================

/**
 * Get all coupons (admin only)
 */
export async function getAdminCoupons(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
}): Promise<ApiResponse<{ data: Coupon[]; pagination: PaginationMeta }>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/coupons`, params as Record<string, unknown>));
}

/**
 * Create a new coupon (admin only)
 */
export async function createAdminCoupon(data: {
  code: string;
  description?: string;
  discountType: 'percent' | 'flat';
  discountValue: number;
  maxUses?: number | null;
  maxUsesPerUser?: number;
  validFrom: string;
  validUntil: string;
}): Promise<ApiResponse<Coupon>> {
  return apiFetch(`${PROXY_BASE}/admin/coupons`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a coupon (admin only)
 */
export async function updateAdminCoupon(id: string, data: Partial<{
  code: string;
  description: string;
  discountType: 'percent' | 'flat';
  discountValue: number;
  maxUses: number | null;
  maxUsesPerUser: number;
  validFrom: string;
  validUntil: string;
  isActive: boolean;
}>): Promise<ApiResponse<Coupon>> {
  return apiFetch(`${PROXY_BASE}/admin/coupons/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Get coupon redemptions (admin only)
 */
export async function getAdminCouponRedemptions(couponId: string, params?: {
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<{ data: CouponRedemption[]; pagination: PaginationMeta }>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/coupons/${couponId}/redemptions`, params as Record<string, unknown>));
}

// Pods - re-exported from features/wardrobe/api above

// ============================================
// Config
// ============================================

export interface DressingType {
  tone: string;
  type: string;
  why: string;
}

export interface Occasion {
  id: string;
  name: string;
  icon?: string;
  dressDescription?: string;
}

export interface Category {
  id: string;
  name: string;
  subcategories: Array<{ id: string; name: string }>;
}

export interface ColorFamily {
  id: string;
  name: string;
  hex: string;
}

/**
 * Get all dressing types
 */
export async function getDressingTypes(): Promise<ApiResponse<DressingType[]>> {
  return apiFetch(`${PROXY_BASE}/config/dressing-types`);
}

/**
 * Get all occasions/events
 */
export async function getOccasions(): Promise<ApiResponse<Occasion[]>> {
  return apiFetch(`${PROXY_BASE}/config/occasions`);
}

/**
 * Get all categories with subcategories
 */
export async function getCategories(): Promise<ApiResponse<Category[]>> {
  return apiFetch(`${PROXY_BASE}/config/categories`);
}

/**
 * Get all color families
 */
export async function getColorFamilies(): Promise<ApiResponse<ColorFamily[]>> {
  return apiFetch(`${PROXY_BASE}/config/color-families`);
}

// ============================================
// Admin
// ============================================

export interface AdminAnalytics {
  total_users: number;
  active_users_today: number;
  new_users_this_week: number;
  total_wardrobe_items: number;
  total_ai_generations: number;
  revenue_this_month: number;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  coins: number;
  creditBalance: number;
  hasOnboarded: boolean;
  onboardingStep: number;
  onboardingComplete: boolean;
  aiFeatureUseCount: number;
  isActive: boolean;
  joined: string;
  lastLoginAt: string | null;
  itemCount: number;
  generationCount: number;
}

export interface AdminOverview {
  totalUsers: number;
  dau: number;
  wau: number;
  mau: number;
  newUsersThisWeek: number;
  userGrowthPercent: number;
  totalWardrobeItems: number;
  totalOutfits: number;
  totalAiGenerations: number;
  revenue: number;
  revenueGrowthPercent: number;
  totalCreditsSpent: number;
  onboardedUsers: number;
  onboardingRate: number;
}

export interface AdminFeatureUsage {
  featureCode: string;
  featureName: string;
  usageCount: number;
  uniqueUsers: number;
  creditsConsumed: number;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export interface AdminAiCostEntry {
  date: string;
  provider: string;
  modelName: string;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number;
}

export interface AdminAiCosts {
  daily: AdminAiCostEntry[];
  providers: Array<{ provider: string; callCount: number; inputTokens: number; outputTokens: number }>;
  models: Array<{ modelName: string; callCount: number; inputTokens: number; outputTokens: number }>;
}

export interface AdminRevenue {
  dailyRevenue: Array<{ date: string; revenue: number; transactions: number }>;
  revenueByPlan: Array<{ planName: string; revenue: number; count: number }>;
  statusBreakdown: Array<{ status: string; count: number; total: number }>;
  totalRevenue: number;
  totalTransactions: number;
  avgTransactionValue: number;
}

export interface AdminUserGrowth {
  dailySignups: Array<{ date: string; count: number }>;
  genderDistribution: Array<{ gender: string; count: number }>;
  bodyTypeDistribution: Array<{ bodyType: string; count: number }>;
  cumulativeUsers: Array<{ date: string; totalUsers: number }>;
  totalUsers: number;
}

export interface AdminWardrobeAnalytics {
  totalItems: number;
  categoryDistribution: Array<{ category: string; count: number }>;
  colorDistribution: Array<{ colorFamily: string; count: number }>;
  dailyUploads: Array<{ date: string; count: number }>;
  uploadSuccessRate: number;
  avgItemsPerUser: number;
}

export interface AdminSystemHealth {
  services: {
    api: { status: string; uptime: number };
    database: { status: string };
  };
  aiCallsByProvider: Array<{ provider: string; callCount: number; inputTokens: number; outputTokens: number }>;
  failedPayments: number;
  failedUploads: number;
  avgTokensPerCall: { input: number; output: number };
  totalAiCalls24h: number;
}

// ============================================
// V2 Admin Analytics Types
// ============================================

export interface AdminOverviewV2 {
  days: number;
  pulse: {
    dataAsOf: string;
    totalUsers: number;
    dau: number;
    dauYesterday: number;
    wau: number;
    mau: number;
    dauMauRatio: number;
    newUsersToday: number;
    newUsersInPeriod: number;
    userGrowthPercent: number;
    revenuePeriod: number;
    revenueToday: number;
    revenueGrowthPercent: number;
    payingUsers: number;
    totalAiCallsToday: number;
    creditsSpentToday: number;
    onboardingRate: number;
    onboardedUsers: number;
    onboardingCompleteUsers: number;
  };
  charts: {
    dailySignups: Array<{ date: string; count: number }>;
    dailyRevenue: Array<{ date: string; revenue: number; transactions: number }>;
    dailyAiCalls: Array<{ date: string; calls: number }>;
  };
  featureBreakdown: Array<{
    code: string;
    name: string;
    usesToday: number;
    usesWeek: number;
    uniqueUsers: number;
    credits: number;
    avgTokens: number;
  }>;
  topUsers: Array<{
    id: string;
    name: string;
    email: string;
    creditsSpent: number;
    aiCalls: number;
  }>;
}

export interface AdminFunnel {
  funnel: Array<{
    stage: string;
    count: number;
    conversionFromPrev: number;
  }>;
}

export interface AdminRetention {
  cohorts: Array<Record<string, unknown>>;
}

/**
 * Where users connect from, derived from the request IP (users.detectedCountry
 * / detectedRegion / detectedCity), NOT from anything the user typed.
 *
 * `percent` on both lists is a share of `resolvedUsers`, not `totalUsers` —
 * read it together with `coveragePercent`, which says how much of the userbase
 * has been placed at all.
 */
export interface AdminGeography {
  totalUsers: number;
  resolvedUsers: number;
  unresolvedUsers: number;
  coveragePercent: number;
  countries: Array<{
    /** ISO 3166-1 alpha-2, e.g. "IN" */
    country: string;
    users: number;
    percent: number;
  }>;
  cities: Array<{
    city: string;
    region: string | null;
    country: string | null;
    users: number;
    percent: number;
  }>;
}

export interface AdminHealthCheck {
  api: { status: string };
  db: { status: string; latencyMs: number };
  redis: { status: string };
  ai: { status: string; callsLastHour: number };
}

export interface AdminTimelineEntry {
  type: string;
  timestamp: string;
  description: string;
  featureCode?: string | null;
  amount?: number;
  metadata?: Record<string, unknown>;
}

export interface AdminUserActivity {
  user: {
    id: string;
    name: string | null;
    email: string;
    role: string;
    coins: number;
    creditBalance: number;
    totalCreditsUsed: number;
    hasOnboarded: boolean;
    onboardingStep: number;
    onboardingComplete: boolean;
    aiFeatureUseCount: number;
    gender: string | null;
    dateOfBirth: string | null;
    profileImageUrl: string | null;
    createdAt: string;
    lastLoginAt: string | null;
    isActive: boolean;
  };
  summary: {
    totalCreditsSpent: number;
    totalAiCalls: number;
    totalWardrobeItems: number;
    totalOutfits: number;
    featureBreakdown: Array<{ featureCode: string; featureName: string; count: number; creditsSpent: number }>;
    favoriteFeature: string;
    lastActiveAt: string;
  };
  timeline: AdminTimelineEntry[];
  creditHistory: Array<{
    id: string;
    amount: number;
    balanceBefore: number;
    balanceAfter: number;
    transactionType: string;
    featureCode: string | null;
    featureName: string | null;
    description: string | null;
    createdAt: string;
  }>;
  payments: Array<{
    id: string;
    amount: number;
    status: string;
    planName: string | null;
    creditsGranted: number;
    createdAt: string;
  }>;
  wardrobeItems: Array<{
    id: string;
    name: string;
    category: string;
    imageUrl: string | null;
    catalogUrl: string | null; // AI-generated catalogue image (transparent bg)
    createdAt: string;
  }>;
  outfits: Array<{
    id: string;
    name: string;
    mood: string | null;
    occasion: string | null;
    imageUrl: string | null;
    createdAt: string;
  }>;
}

/**
 * Get platform analytics (admin only) — legacy
 */
export async function getAdminAnalytics(): Promise<ApiResponse<AdminAnalytics>> {
  return apiFetch(`${PROXY_BASE}/admin/analytics`);
}

/**
 * Get admin overview (new)
 */
export async function getAdminOverview(params?: { from?: string; to?: string }): Promise<ApiResponse<AdminOverview>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/overview`, params as Record<string, unknown>));
}

/**
 * Get feature usage analytics
 */
export async function getAdminFeatureUsage(params?: { from?: string; to?: string }): Promise<ApiResponse<AdminFeatureUsage[]>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/features`, params as Record<string, unknown>));
}

/**
 * Get AI cost analytics
 */
export async function getAdminAiCosts(params?: { from?: string; to?: string }): Promise<ApiResponse<AdminAiCosts>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/ai-costs`, params as Record<string, unknown>));
}

/**
 * Get revenue analytics
 */
export async function getAdminRevenue(params?: { from?: string; to?: string }): Promise<ApiResponse<AdminRevenue>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/revenue`, params as Record<string, unknown>));
}

/**
 * Get user growth analytics
 */
export async function getAdminUserGrowth(params?: { from?: string; to?: string }): Promise<ApiResponse<AdminUserGrowth>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/users/growth`, params as Record<string, unknown>));
}

/**
 * Get wardrobe analytics
 */
export async function getAdminWardrobeAnalytics(params?: { from?: string; to?: string }): Promise<ApiResponse<AdminWardrobeAnalytics>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/wardrobe`, params as Record<string, unknown>));
}

/**
 * Get system health analytics
 */
export async function getAdminSystemHealth(): Promise<ApiResponse<AdminSystemHealth>> {
  return apiFetch(`${PROXY_BASE}/admin/analytics/health`);
}

/**
 * Get admin overview v2 (DEMO-excluded, new metrics)
 */
export async function getAdminOverviewV2(params?: { days?: number }): Promise<ApiResponse<AdminOverviewV2>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/overview-v2`, params as Record<string, unknown>));
}

/**
 * Get admin funnel analytics
 */
export async function getAdminFunnel(params?: { days?: number }): Promise<ApiResponse<AdminFunnel>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/funnel`, params as Record<string, unknown>));
}

/**
 * Get admin retention cohorts
 */
export async function getAdminRetention(params?: { weeks?: number }): Promise<ApiResponse<AdminRetention>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/analytics/retention`, params as Record<string, unknown>));
}

/**
 * Get the IP-derived breakdown of where users are. Takes no period — this is
 * current state, not a time series (see the backend handler for why).
 */
export async function getAdminGeography(): Promise<ApiResponse<AdminGeography>> {
  return apiFetch(`${PROXY_BASE}/admin/analytics/geography`);
}

/**
 * Get admin health check (quick status dots)
 */
export async function getAdminHealthCheck(): Promise<ApiResponse<AdminHealthCheck>> {
  return apiFetch(`${PROXY_BASE}/admin/analytics/health-check`);
}

/**
 * Get all users (admin only)
 */
export async function getAdminUsers(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  role?: string;
  onboarded?: string;
}): Promise<ApiResponse<{ data: AdminUser[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/users`, params as Record<string, unknown>));
}

/**
 * Get specific user (admin only)
 */
export async function getAdminUser(userId: string): Promise<ApiResponse<unknown>> {
  return apiFetch(`${PROXY_BASE}/admin/users/${userId}`);
}

/**
 * Get user activity (admin only)
 */
export async function getAdminUserActivity(userId: string): Promise<ApiResponse<AdminUserActivity>> {
  return apiFetch(`${PROXY_BASE}/admin/users/${userId}/activity`);
}

/**
 * Add credits to a user (admin only)
 */
export async function addCreditsToUser(
  userId: string,
  amount: number,
  reason?: string
): Promise<ApiResponse<{ transactionId: string; newBalance: number }>> {
  return apiFetch(`${PROXY_BASE}/admin/users/${userId}/credits`, {
    method: "POST",
    body: JSON.stringify({ amount, reason }),
  });
}

// ============================================
// Admin Prompts
// ============================================

export interface AdminPromptListItem {
  id: string;
  promptName: string;
  environment: string;
  temperature: number;
  maxTokens: number;
  provider: string;
  model: string | null;
  featureCode: string | null;
  serviceName: string | null;
  category: string | null;
  description: string | null;
  isActive: boolean;
  lastEditedBy: string | null;
  updatedAt: string;
}

export interface AdminPromptFull extends AdminPromptListItem {
  promptText: string;
  systemMessage: string | null;
  variables: string[];
  availableVariables: Array<{ name: string; description: string; namespace: string }> | null;
  createdAt: string;
}

export interface AdminPromptUpdateData {
  environment: string;
  promptText?: string;
  systemMessage?: string | null;
  temperature?: number;
  maxTokens?: number;
  provider?: string;
  model?: string | null;
  description?: string | null;
  isActive?: boolean;
}

export interface AdminPromptTestResult {
  builtPrompt: string;
  charCount: number;
  systemMessage: string | null;
  config: {
    temperature: number;
    maxTokens: number;
    provider: string;
    model: string | null;
  };
}

export async function getAdminPrompts(params: {
  environment: string;
  category?: string;
}): Promise<ApiResponse<AdminPromptListItem[]>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/prompts`, params as Record<string, unknown>));
}

// ============================================
// AI Response Failures (Admin)
// ============================================

export type AiFailureType =
  | "zod_validation"
  | "json_parse"
  | "empty_response"
  | "other";

export interface AiResponseFailure {
  id: string;
  feature: string;
  model: string;
  provider: string;
  failureType: AiFailureType;
  errorMessage: string;
  rawResponse: string | null;
  promptSnippet: string | null;
  userId: string | null;
  finishReason: string | null;
  createdAt: string;
}

export interface AiFailureListResponse {
  items: AiResponseFailure[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

export interface AiFailureStats {
  windowHours: number;
  total: number;
  mostRecent: string | null;
  byFeature: { feature: string; count: number }[];
  byType: { failureType: AiFailureType; count: number }[];
}

export async function getAiFailures(params?: {
  limit?: number;
  offset?: number;
  feature?: string;
  failureType?: AiFailureType;
  sinceHours?: number;
}): Promise<ApiResponse<AiFailureListResponse>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/ai-failures`, params as Record<string, unknown>));
}

export async function getAiFailureStats(sinceHours = 24): Promise<ApiResponse<AiFailureStats>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/ai-failures/stats`, { sinceHours }));
}

export async function getAiFailureById(id: string): Promise<ApiResponse<AiResponseFailure>> {
  return apiFetch(`${PROXY_BASE}/admin/ai-failures/${encodeURIComponent(id)}`);
}

// ============================================
// AI Feedback — thumbs up/down on AI-generated results
// ============================================

export type AiFeedbackRating = "up" | "down";

export interface AiFeedbackItem {
  id: string;
  userId: string;
  feature: string;
  featureCode: string | null;
  generationId: string;
  rating: AiFeedbackRating;
  comment: string | null;
  createdAt: string;
}

export interface AiFeedbackStats {
  windowHours: number;
  total: number;
  totalUp: number;
  totalDown: number;
  upRate: number | null;
  byFeature: { feature: string; up: number; down: number; total: number; upRate: number | null }[];
  recentDown: Pick<AiFeedbackItem, "id" | "userId" | "feature" | "generationId" | "comment" | "createdAt">[];
}

export async function submitAiFeedback(input: {
  feature: string;
  featureCode?: string;
  generationId: string;
  rating: AiFeedbackRating;
  comment?: string;
}): Promise<ApiResponse<{ id: string; rating: AiFeedbackRating }>> {
  return apiFetch(`${PROXY_BASE}/ai-feedback`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getAiFeedbackStats(sinceHours = 168): Promise<ApiResponse<AiFeedbackStats>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/ai-feedback/stats`, { sinceHours }));
}

export async function getAiFeedback(params?: {
  limit?: number;
  offset?: number;
  rating?: AiFeedbackRating;
  feature?: string;
  sinceHours?: number;
}): Promise<ApiResponse<{ items: AiFeedbackItem[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/ai-feedback`, params as Record<string, unknown>));
}

export async function getAdminPrompt(
  promptName: string,
  environment: string
): Promise<ApiResponse<AdminPromptFull>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/prompts/${encodeURIComponent(promptName)}`, { environment }));
}

export async function updateAdminPrompt(
  promptName: string,
  data: AdminPromptUpdateData
): Promise<ApiResponse<AdminPromptFull>> {
  return apiFetch(`${PROXY_BASE}/admin/prompts/${encodeURIComponent(promptName)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function testAdminPrompt(
  promptName: string,
  data: { environment: string; variables: Record<string, string> }
): Promise<ApiResponse<AdminPromptTestResult>> {
  return apiFetch(`${PROXY_BASE}/admin/prompts/${encodeURIComponent(promptName)}/test`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function invalidateAdminPromptCache(): Promise<ApiResponse<{ message: string }>> {
  return apiFetch(`${PROXY_BASE}/admin/prompts/invalidate-cache`, {
    method: "POST",
  });
}

// ============================================
// Tarot Cards
// ============================================

export interface TarotCard {
  id: string;
  userId: string;
  aiMsg: string;
  cardImgUrl: string;
  msgCardImgUrl: string | null;
  noOfOutfit: number;
  archetype: string;
  viewStatus: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TarotCardGenerateResponse {
  id: string;
  userId: string;
  archetype: string;
  aiMsg: string;
  cardImgUrl: string;
  msgCardImgUrl: string | null;
  noOfOutfit: number;
  visualMoodBoard: {
    dominant_colors: string[];
    color_hex_codes: string[];
    textures: string[];
    shapes: string[];
    energy: string;
  };
  createdAt: string;
}

/**
 * Generate a new tarot card for the user
 */
export async function generateTarotCard(): Promise<ApiResponse<TarotCardGenerateResponse>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/generate`, {
    method: "POST",
    timeout: AI_TIMEOUT,
  });
}

/**
 * Get all tarot cards for a user
 */
export async function getTarotCards(userId: string): Promise<ApiResponse<TarotCard[]>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/user/${userId}`);
}

/**
 * Get the latest tarot card for a user
 */
export async function getLatestTarotCard(userId: string): Promise<ApiResponse<TarotCard>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/latest/${userId}`);
}

/**
 * Check if user has unseen tarot card
 */
export async function hasUnseenTarotCard(userId: string): Promise<ApiResponse<{ hasUnseen: boolean }>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/has-unseen/${userId}`);
}

/**
 * Mark user's tarot cards as seen
 */
export async function markTarotCardsAsSeen(userId: string): Promise<ApiResponse<{ message: string }>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/mark-seen/${userId}`, {
    method: "PATCH",
  });
}

/**
 * Get a tarot card by ID
 */
export async function getTarotCardById(id: string): Promise<ApiResponse<TarotCard>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/${id}`);
}

/**
 * Delete a tarot card
 */
export async function deleteTarotCard(id: string): Promise<ApiResponse<TarotCard>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/${id}`, {
    method: "DELETE",
  });
}

// ── Daily Reward Types ──────────────────────────────────────────

export interface DailyRewardStatus {
  unclaimedCards: number;
  hasClaimedToday: boolean;
  currentStreak: number;
  longestStreak: number;
  totalClaims: number;
  nextMilestone: number;
  bonusCardsPending: number;
}

export interface DailyRewardClaimResponse {
  card: TarotCardGenerateResponse;
  rewardStatus: DailyRewardStatus;
  isBonus: boolean;
}

/**
 * Get daily reward status for the authenticated user
 */
export async function getDailyRewardStatus(): Promise<ApiResponse<DailyRewardStatus>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/daily-reward/status`);
}

/**
 * Claim a daily reward card
 */
export async function claimDailyReward(): Promise<ApiResponse<DailyRewardClaimResponse>> {
  return apiFetch(`${PROXY_BASE}/tarot-cards/daily-reward/claim`, {
    method: "POST",
    timeout: AI_TIMEOUT,
  });
}

// ============================================
// Admin Marketing Links
// ============================================

export interface MarketingLink {
  id: string;
  code: string;
  label: string;
  destinationUrl: string;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  clickCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingLinkAnalytics {
  daily: Array<{ date: string; clicks: number }>;
  devices: Array<{ device: string; clicks: number }>;
  browsers: Array<{ browser: string; clicks: number }>;
  referrers: Array<{ referrer: string; clicks: number }>;
}

export interface MarketingOverview {
  totalClicks: number;
  totalLinks: number;
  activeLinks: number;
  daily: Array<{ date: string; clicks: number }>;
}

export async function getAdminMarketingLinks(params?: {
  page?: number;
  limit?: number;
}): Promise<ApiResponse<{ links: MarketingLink[]; total: number; page: number; limit: number }>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/marketing-links`, params as Record<string, unknown>));
}

export async function createAdminMarketingLink(data: {
  label: string;
  destinationUrl: string;
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
}): Promise<ApiResponse<MarketingLink>> {
  return apiFetch(`${PROXY_BASE}/admin/marketing-links`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdminMarketingLink(
  id: string,
  data: { label?: string; isActive?: boolean }
): Promise<ApiResponse<MarketingLink>> {
  return apiFetch(`${PROXY_BASE}/admin/marketing-links/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminMarketingLink(id: string): Promise<ApiResponse<void>> {
  return apiFetch(`${PROXY_BASE}/admin/marketing-links/${id}`, {
    method: "DELETE",
  });
}

export async function getAdminMarketingLinkAnalytics(
  id: string,
  params?: { days?: number }
): Promise<ApiResponse<MarketingLinkAnalytics>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/marketing-links/${id}/analytics`, params as Record<string, unknown>));
}

export async function getAdminMarketingOverview(params?: {
  days?: number;
}): Promise<ApiResponse<MarketingOverview>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/marketing-links/analytics/overview`, params as Record<string, unknown>));
}

// ============================================
// Admin — Merchant Tenants
// ============================================

export interface MerchantTenantSummary {
  id: string;
  tenantName: string;
  platform: 'shopify' | 'woocommerce';
  merchantDomain: string;
  isActive: boolean;
  productCount: number;
  lastReconciledAt: string | null;
  createdAt: string;
  // True when apiCredentialsEncrypted is set. False means we can receive
  // webhooks but can't reconcile / pull the catalog. The admin merchants
  // page flags these tenants so they don't get silently un-syncable.
  hasCredentials: boolean;
}

export interface MerchantTenantDetail extends MerchantTenantSummary {
  merchantBaseUrl: string;
  reconciliationPeriod: number;
  tenantConfig: Record<string, unknown> | null;
  updatedAt: string;
}

export interface MerchantWebhookLog {
  id: string;
  tenantId: string;
  eventType: string;
  shopifyObjectId: string | null;
  status: string;
  failedReason: string | null;
  rawPayload: unknown;
  processingTimeMs: number | null;
  createdAt: string;
}

export interface MerchantTenantWithLogs extends MerchantTenantDetail {
  webhookLogs: MerchantWebhookLog[];
}

export interface MerchantsSummary {
  totalTenants: number;
  activeTenants: number;
  totalProducts: number;
  viewsLast7d: number;
  clicksLast7d: number;
  revenueLast30dVerified: number;
}

export interface MerchantAnalytics {
  tenantId: string;
  tenantName: string;
  platform: 'shopify' | 'woocommerce';
  productCount: number;
  productCountTotal: number;
  needsReviewCount: number;
  viewsLast7d: number;
  viewsLast30d: number;
  clicksLast7d: number;
  clicksLast30d: number;
  ordersLast30d: number;
  revenueLast30d: number;
  revenueLast30dVerified: number;
  lastReconciledAt: string | null;
  lastReconciliationStatus: string | null;
}

export type MerchantCredentialsInput =
  | { clientId: string; clientSecret: string }
  | { accessToken: string }
  | { consumerKey: string; consumerSecret: string };

export interface CreateMerchantInput {
  tenantName: string;
  platform: 'shopify' | 'woocommerce';
  merchantDomain: string;
  merchantBaseUrl: string;
  apiCredentials: MerchantCredentialsInput;
  tenantConfig?: Record<string, unknown>;
  reconciliationPeriod?: number;
}

export interface UpdateMerchantInput {
  isActive?: boolean;
  reconciliationPeriod?: number;
  tenantConfig?: Record<string, unknown>;
  merchantBaseUrl?: string;
  apiCredentials?: MerchantCredentialsInput;
}

export async function getAdminMerchants(): Promise<ApiResponse<MerchantTenantSummary[]>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants`);
}

export async function getAdminMerchantsSummary(): Promise<ApiResponse<MerchantsSummary>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/summary`);
}

// ---------------------------------------------------------------------------
// Admin catalog curation — block products from ever appearing in the feed.
// ---------------------------------------------------------------------------

export interface AdminCatalogProduct {
  id: string;
  title: string;
  image: string | null;
  category: string | null;
  subCategory: string | null;
  isActive: boolean;
  blocked: boolean;
  exclusionReason: string | null;
  merchant: string | null;
  merchantId: string | null;
  price: number | null;
  currency: string;
}

export interface AdminCatalogPage {
  products: AdminCatalogProduct[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export async function getAdminCatalogProducts(params?: {
  tenantId?: string;
  q?: string;
  status?: "active" | "blocked" | "inactive" | "all";
  page?: number;
  pageSize?: number;
}): Promise<ApiResponse<AdminCatalogPage>> {
  const qs = new URLSearchParams();
  if (params?.tenantId) qs.set("tenantId", params.tenantId);
  if (params?.q) qs.set("q", params.q);
  if (params?.status) qs.set("status", params.status);
  if (params?.page) qs.set("page", String(params.page));
  if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch(`${PROXY_BASE}/admin/catalog/products${suffix}`);
}

export async function blockAdminProduct(
  id: string,
  reason?: string,
): Promise<ApiResponse<{ id: string; blocked: boolean; isActive: boolean }>> {
  return apiFetch(`${PROXY_BASE}/admin/catalog/products/${id}/block`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export async function unblockAdminProduct(
  id: string,
): Promise<ApiResponse<{ id: string; blocked: boolean; isActive: boolean }>> {
  return apiFetch(`${PROXY_BASE}/admin/catalog/products/${id}/unblock`, {
    method: "POST",
  });
}

export async function getAdminMerchant(
  id: string
): Promise<ApiResponse<MerchantTenantWithLogs>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/${id}`);
}

export async function createAdminMerchant(
  data: CreateMerchantInput
): Promise<ApiResponse<MerchantTenantDetail> & { warning?: string }> {
  return apiFetch(`${PROXY_BASE}/admin/merchants`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface CreatePendingConnectionInput {
  shopDomain: string;
  tenantName: string;
  clientId: string;
  clientSecret: string;
}

export interface PendingConnectionResult {
  tenant: MerchantTenantDetail;
  installUrl: string;
}

export async function createPendingShopifyConnection(
  data: CreatePendingConnectionInput
): Promise<ApiResponse<PendingConnectionResult>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/connect-shopify`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdminMerchant(
  id: string,
  data: UpdateMerchantInput
): Promise<ApiResponse<MerchantTenantDetail>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminMerchant(
  id: string
): Promise<ApiResponse<MerchantTenantDetail>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/${id}`, {
    method: "DELETE",
  });
}

export async function triggerAdminMerchantReconcile(
  id: string
): Promise<ApiResponse<{ enqueued: boolean; jobId: string }>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/${id}/reconcile`, {
    method: "POST",
  });
}

export async function retryAdminMerchantWebhooks(
  id: string
): Promise<ApiResponse<MerchantTenantDetail>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/${id}/retry-webhooks`, {
    method: "POST",
  });
}

export async function getAdminMerchantWebhookLogs(
  id: string,
  params?: { page?: number; limit?: number; status?: string }
): Promise<ApiResponse<{ rows: MerchantWebhookLog[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/admin/merchants/${id}/logs`, params as Record<string, unknown>));
}

export async function getAdminMerchantAnalytics(
  id: string
): Promise<ApiResponse<MerchantAnalytics>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/${id}/analytics`);
}

export interface MerchantEnrichmentStatus {
  total: number;
  counts: {
    PENDING: number;
    PROCESSING: number;
    COMPLETED: number;
    FAILED: number;
    RATE_LIMITED: number;
    STALE: number;
  };
  inFlight: number;
  percentComplete: number;
  recentFailures: Array<{ id: string; title: string; updatedAt: string }>;
  lastCompletedAt: string | null;
}

export async function getAdminMerchantEnrichmentStatus(
  id: string
): Promise<ApiResponse<MerchantEnrichmentStatus>> {
  return apiFetch(`${PROXY_BASE}/admin/merchants/${id}/enrichment-status`);
}

// ===============================================================
// Admin AI Chat (natural-language → SQL/HogQL via Gemini 2.5 Flash)
// Cross-source: Postgres (system of record) + PostHog (event data).
// ===============================================================

export type AdminAiChartType = "table" | "bar" | "line" | "pie" | "metric";

export type AdminAiQuerySource = "postgres" | "posthog";

export interface AdminAiQueryStep {
  source: AdminAiQuerySource;
  query: string;
  purpose: string;
  rowCount: number;
  error?: string;
}

export interface AdminAiQueryResult {
  question: string;
  sql: string;
  title: string;
  description: string;
  chartType: AdminAiChartType;
  xField: string;
  yField: string;
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  columns: string[];
  executionTimeMs: number;
  sources?: AdminAiQuerySource[];
  steps?: AdminAiQueryStep[];
  /** Present when the query ran inside a persisted conversation. */
  conversationId?: string | null;
}

export async function adminAiQuery(
  question: string,
  conversationId?: string | null
): Promise<ApiResponse<AdminAiQueryResult>> {
  return apiFetch(`${PROXY_BASE}/admin/ai-query`, {
    method: "POST",
    body: JSON.stringify(
      conversationId ? { question, conversationId } : { question }
    ),
  });
}

// ===============================================================
// Admin AI Chat — conversation history (ChatGPT-style threads)
// ===============================================================

export interface AdminChatConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface AdminChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Populated for assistant messages — full AdminAiQueryResult frozen at send time */
  result: AdminAiQueryResult | null;
  createdAt: string;
}

export interface AdminChatConversationFull {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: AdminChatMessage[];
}

export async function listAdminChatConversations(): Promise<
  ApiResponse<AdminChatConversationSummary[]>
> {
  return apiFetch(`${PROXY_BASE}/admin/ai-query/conversations`);
}

export async function createAdminChatConversation(
  title?: string
): Promise<ApiResponse<AdminChatConversationSummary>> {
  return apiFetch(`${PROXY_BASE}/admin/ai-query/conversations`, {
    method: "POST",
    body: JSON.stringify(title ? { title } : {}),
  });
}

export async function getAdminChatConversation(
  id: string
): Promise<ApiResponse<AdminChatConversationFull>> {
  return apiFetch(`${PROXY_BASE}/admin/ai-query/conversations/${id}`);
}

export async function renameAdminChatConversation(
  id: string,
  title: string
): Promise<ApiResponse<{ id: string; title: string; updatedAt: string }>> {
  return apiFetch(`${PROXY_BASE}/admin/ai-query/conversations/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

export async function deleteAdminChatConversation(
  id: string
): Promise<ApiResponse<null>> {
  return apiFetch(`${PROXY_BASE}/admin/ai-query/conversations/${id}`, {
    method: "DELETE",
  });
}

// Set Detection & Processing - re-exported from features/wardrobe/api above
